import type { QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

type Meta = {
  continuation?: {
    first?: string;
    last?: string;
  };
  pagination?: {
    page: number;
    total: number;
  };
};

/**
 * @deprecated Will be replaced by ts-rest's useInfiniteQuery post-hydra merge.
 *
 * This hook can be used to paginate a static list of values.
 * It can be passed directly to a Paginator component, like so:
 *
 * ```
 * const query = usePaginatedQuery({
 *   queryFn: ({ meta }) => {
 *     return fetch('/api/destinations', { params: meta.pagination });)
 *   }
 * });
 *
 * return (
 *   <Pagination {...query.pagination} />
 * );
 * ```
 */

const DEFAULT_PAGE_SIZE = 10;

type Query<T> = UseQueryResult<T & { meta: Meta }>;
type QueryOptions<T> = UseQueryOptions<T & { meta: Meta }>;
type ExtendedQueryResult<T> = Query<T> & {
  pagination: PaginationResult;
};

export type PaginationRequest = {
  limit?: number;
  page?: number;
};
export type PaginationResult = {
  page: number;
  pageSize: number;
  totalItemCount: number;
  onPageChange: (page: number) => void;
  reset: (page?: number) => void;
};

export function usePaginatedQuery<T>(options: QueryOptions<T>, { initialPage = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const [page, setPage] = useState(initialPage);
  const pagination = useRef({ page, pageSize, totalItemCount: 0 });

  // Update the paginator as soon as the page changes. (ie: the user requested another page)
  useEffect(() => {
    pagination.current.page = page;
  }, [page]);

  const query = useQuery({
    ...options,
    meta: {
      pagination: {
        limit: pageSize,
        page,
      },
    },
    queryKey: [
      ...(options.queryKey ?? []),
      {
        limit: pageSize,
        page,
      },
    ] as QueryKey,
    placeholderData: keepPreviousData,
  });

  // Update the paginator when the query returns new results.
  if (query.data?.meta?.pagination != null) {
    pagination.current = {
      page: query.data.meta.pagination.page,
      totalItemCount: query.data.meta.pagination.total,
      pageSize,
    };
  }

  const extendedQuery = query as ExtendedQueryResult<T>;

  // Add pagination to the query, so it can be used with the Paginator component.
  extendedQuery.pagination = {
    ...pagination.current,
    onPageChange: setPage,
    reset: (page = 1) => setPage(page),
  };

  return extendedQuery;
}
